/**
 * Which projects the client-page "Open Projects" panel shows, and what a ?openProject= deep link
 * has to reveal in order for its target to be on screen.
 *
 * The panel has three views:
 *   - default            → open projects that are current (see isFutureProject)
 *   - "Show future"      → adds open projects that have not started yet / are due further out
 *   - "Closed"/All       → adds closed + completed projects
 *
 * A deep link (notification email, bell, search, Projects table) can point at a project any of
 * those views hides, so the page widens the view for it. It must widen ONLY when the target is
 * genuinely hidden: a plain chevron expand writes the same ?openProject= param, and widening on
 * that flipped every client page to "All Projects" — surfacing years of closed work.
 */

// projects due more than these days is marked "future"
export const FUTURE_PROJECT_DAYS = 14;

// A project is "future" when it hasn't started yet. Its start date decides when it has one:
// once the start date arrives it is current work, however far out the due date is.
// With no start date, it falls back to the due date being more than FUTURE_PROJECT_DAYS away.
export function isFutureProject(
  p: { start_date?: string | null; due_date?: string | null },
  now: number = Date.now(),
): boolean {
  if (p.start_date) {
    const d = new Date(now);
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return p.start_date.slice(0, 10) > today;
  }
  const futureThreshold = now + FUTURE_PROJECT_DAYS * 24 * 60 * 60 * 1000;
  // due <= threshold is current, due > threshold is future
  return !!p.due_date && new Date(p.due_date).getTime() > futureThreshold;
}

// what should be revealed
export type DeepLinkReveal = "all" | "future" | null;

export function deepLinkReveal(
  target: { status?: string | null; start_date?: string | null; due_date?: string | null; deleted_at?: string | null } | undefined | null,
  now: number = Date.now(),
): DeepLinkReveal {
  // widening the view would not surface them.
  if (!target || target.deleted_at) return null;
  if (target.status === "closed" || target.status === "completed") return "all";
  if (isFutureProject(target, now)) return "future";
  return null;
}
